import React, { createContext, useContext, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Routes, Route, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
    Alert,
    AppBar,
    CssBaseline,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemText, Snackbar,
    Stack,
    ThemeProvider,
    Toolbar,
} from '@mui/material';
import { mainTheme } from '../themes/mainTheme';
import EvaluationPage from './EvaluationPage';
import PostAddIcon from '@mui/icons-material/PostAdd';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Course } from '../types/courseTypes';
import { deleteCourse, fetchAllCourses } from '../services/MentorApiService';
import CourseDialog from './CourseDialog';
import CourseInfoForm from './CourseInfoForm';
import NotFound from './NotFound';
import CourseRouteGuard from './CourseRouteGuard';
import Home from './Home';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import ConfirmDialog from './ConfirmDialog';
import SuggestionPage from './SuggestionPage';
import SAILLogo from '../SAIL.svg';
import CSSELogo from '../CSSE-Logo-Black.svg'
import CourseChatTable from './ChatCatalog';
import {SnackbarContextType, SnackbarState} from '../types/snackbarTypes';
import localization from '../locals/en.json'

interface NavigationItem {
    kind?: 'divider';
    segment?: string;
    title: string;
    icon?: React.ReactNode;
    action?: React.ReactNode;
}

const SnackbarContext = createContext<SnackbarContextType>({
    showSnackbar: () => console.warn('SnackbarContext used outside of Provider'),
});

export const useSnackbar = (): SnackbarContextType => {
    return useContext(SnackbarContext);
};

const DashBoard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [courses, setCourses] = useState<Course[]>([]);
    const [conversationModalOpen, setCourseDialogOpen] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [conversation, setCourse] = useState<Course | null>(null);
    const [courseId, setCourseId] = useState<number>(0);
    const [isDrawerExpanded, setIsDrawerExpanded] = useState(true);
    const [isCoursesExpanded, setIsCoursesExpanded] = useState(true);
    const [isCoursesHovered, setIsCoursesHovered] = useState(false);
    const [isLogoHovered, setIsLogoHovered] = useState(false);
    const [snackbarState, setSnackbarState] = useState<SnackbarState>({
        open: false,
        message: '',
        severity: 'info',
    });
    const drawerWidth = isDrawerExpanded ? 240 : 56;

    const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
        setSnackbarState({ open: true, message, severity });
    };

    const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        setSnackbarState((prev) => ({ ...prev, open: false }));
    };

    const deleteDialog = {
        title: localization.courseForm.action.delete.title,
        description: localization.courseForm.action.delete.instructions,
        confirmButtonText: localization.courseForm.action.delete.confirm,
    };

    const handleFetchCourses = () => {
        fetchAllCourses()
            .then((response) => {
                setCourses(response.data.courses);
            })
            .catch((error) => {
                showSnackbar('Failed to fetch courses', 'error');
            });
    };

    const handleDeleteCourse = () => {
        deleteCourse(courseId)
            .then(() => {
                handleFetchCourses();
                showSnackbar('Course deleted successfully', 'info');
                navigate('/home');
            })
            .catch((error) => {
                showSnackbar('Failed to delete course', 'error');
            });
    };

    const handleOpenEditModal = (conv: Course) => {
        setCourse(conv);
        setCourseDialogOpen(true);
    };

    const handleCourseDialogClose = () => {
        setCourseDialogOpen(false);
        setCourse(null);
        handleFetchCourses();
    };

    const handleConversationModalCreate = (courseId: number) => {
        handleCourseDialogClose();
        navigate(`/courses/${courseId}`);
    };

    const handleConfirmDialogOpen = (convId: number) => {
        setCourseId(convId);
        setConfirmDialogOpen(true);
    };

    const handleConfirmDialogClose = () => {
        setConfirmDialogOpen(false);
    };

    const handleCoursePlanFormReturn = () => {
        navigate(-1);
    };

    const handleDrawerToggle = () => {
        setIsDrawerExpanded((prev) => {
            if (!prev) setIsCoursesExpanded(false);
            return !prev;
        });
    };

    React.useEffect(() => {
        if (location.pathname === '/') {
            navigate('/home', { replace: true });
        }
        handleFetchCourses();
    }, [location, navigate]);

    const courseAction = (id: number, conv: Course) => (
        <Stack
            className="hover-buttons"
            direction="row"
            sx={{
                opacity: 0,
                transition: 'opacity 0.2s',
                '&:hover': { opacity: 1 },
            }}
        >
            <IconButton
                size="small"
                onClick={(e) => {
                    e.stopPropagation();
                    handleOpenEditModal(conv);
                }}
            >
                <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
                size="small"
                onClick={(e) => {
                    e.stopPropagation();
                    handleConfirmDialogOpen(conv.id);
                }}
            >
                <DeleteIcon fontSize="small" />
            </IconButton>
        </Stack>
    );

    const NAVIGATION: NavigationItem[] = [
        {
            segment: 'home',
            title: 'Home',
            icon: <HomeOutlinedIcon />,
        },
        {
            title: 'Add Course',
            icon: <PostAddIcon />,
        },
        {
            title: 'Courses',
            icon: <FolderOutlinedIcon />,
        },
        ...(isDrawerExpanded && isCoursesExpanded
            ? courses.map((course) => ({
                segment: `courses/${course.id}`,
                title: course.courseName,
                action: courseAction(course.id, course),
            }))
            : []),
    ];

    const renderPageItem = React.useCallback(
        (item: NavigationItem, index: number) => {
            const isCourseItem = item.segment?.startsWith('courses/');
            const isCoursesHeader = item.title === 'Courses';
            const isBoldItem = !isCourseItem;
            return (
                <Box
                    key={item.segment || `item-${item.title}-${index}`}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        '&:hover .hover-buttons': {
                            opacity: 1,
                        },
                        ml: isCourseItem ? 3 : 0,
                        borderLeft: isCourseItem ? '2px solid' : 'none',
                        borderColor: isCourseItem ? 'text.primary' : 'transparent',
                        pl: isCourseItem ? 1 : 0,
                    }}
                    onMouseEnter={isCoursesHeader ? () => setIsCoursesHovered(true) : undefined}
                    onMouseLeave={isCoursesHeader ? () => setIsCoursesHovered(false) : undefined}
                >
                    <ListItemButton
                        component={item.segment ? RouterLink : 'button'}
                        to={item.segment ? `/${item.segment}` : undefined}
                        onClick={
                            item.title === 'Add Course'
                                ? () => setCourseDialogOpen(true)
                                : isCoursesHeader && isDrawerExpanded
                                    ? () => setIsCoursesExpanded((prev) => !prev)
                                    : undefined
                        }
                        disabled={isCoursesHeader && !isCoursesExpanded && courses.length === 0}
                        sx={{
                            flexGrow: 1,
                            display: 'flex',
                            alignItems: 'center',
                            py: 0.5,
                            '&.Mui-disabled': {
                                color: 'grey.800',
                                opacity: 1,
                            },
                        }}
                    >
                        {item.icon && (
                            <Box
                                sx={{
                                    mr: isDrawerExpanded ? 1 : 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 24,
                                    height: 24,
                                    flexShrink: 0,
                                }}
                            >
                                {isCoursesHeader && isDrawerExpanded ? (
                                    isCoursesHovered ? (
                                        <KeyboardArrowDownIcon
                                            fontSize="small"
                                            className="hover-buttons"
                                            sx={{ opacity: 0, transition: 'opacity 0.2s' }}
                                        />
                                    ) : (
                                        <FolderOutlinedIcon />
                                    )
                                ) : (
                                    item.icon
                                )}
                            </Box>
                        )}
                        <ListItemText
                            primary={item.title}
                            primaryTypographyProps={{
                                sx: {
                                    fontWeight: isBoldItem ? 'bold' : 'normal',
                                    fontSize: '0.875rem',
                                    color: isCourseItem ? 'text.primary' : 'grey.800',
                                    display: isDrawerExpanded ? 'block' : 'none',
                                    lineHeight: '24px',
                                    overflowX: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                },
                            }}
                        />
                    </ListItemButton>
                    {item.action && isDrawerExpanded && (
                        <Box sx={{ flexShrink: 0 }}>{item.action}</Box>
                    )}
                </Box>
            );
        },
        [isDrawerExpanded, isCoursesHovered]
    );

    const drawer = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            {/* Top Section (SAIL Logo and Toggle Button) */}
            <Box
                sx={{
                    p: 2,
                    '&:hover .hover-buttons': {
                        opacity: 1,
                    },
                    flexShrink: 0, // Prevent shrinking
                }}
                onMouseEnter={() => setIsLogoHovered(true)}
                onMouseLeave={() => setIsLogoHovered(false)}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: isDrawerExpanded ? 'auto' : 25,
                            height: 25,
                        }}
                    >
                        {isDrawerExpanded || !isLogoHovered ? (
                            <img
                                src={SAILLogo}
                                alt="SAIL Mentor"
                                style={{ maxHeight: '25px', height: '25px' }}
                            />
                        ) : (
                            <IconButton
                                onClick={handleDrawerToggle}
                                className="hover-buttons"
                                sx={{ opacity: 0, transition: 'opacity 0.2s', p: 0 }}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}
                    </Box>
                    {isDrawerExpanded && (
                        <IconButton onClick={handleDrawerToggle}>
                            <MenuOpenIcon />
                        </IconButton>
                    )}
                </Stack>
            </Box>

            {/* Middle Section (Scrollable Navigation List with Fade) */}
            <Box
                sx={{
                    flexGrow: 1, // Take available space
                    overflowY: 'auto', // Enable vertical scrolling
                    overscrollBehavior: 'contain', // Prevent scroll propagation
                    position: 'relative', // For positioning the fade
                }}
            >
                <List sx={{ paddingBottom: '40px' /* Space for fade effect */ }}>
                    {NAVIGATION.map((item, index) => renderPageItem(item, index))}
                </List>
                {/* Fade Effect */}
                <Box
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        height: '100px', // Height of the fade
                        background: (theme) =>
                            `linear-gradient(to bottom, transparent, ${theme.palette.background.paper})`,
                        pointerEvents: 'none', // Allow clicks to pass through
                    }}
                />
            </Box>

            {/* Bottom Section (CSSE Logo) */}
            <Box
                sx={{
                    p: 2,
                    display: isDrawerExpanded ? 'flex' : 'none',
                    justifyContent: isDrawerExpanded ? 'flex-start' : 'center',
                    flexShrink: 0, // Prevent shrinking
                }}
            >
                <img
                    src={CSSELogo}
                    alt="CSSE"
                    style={{ maxHeight: '40px', height: '40px' }}
                />
            </Box>
        </Box>
    );

    return (
        <ThemeProvider theme={mainTheme}>
            <CssBaseline />
            <Box sx={{ display: 'flex' }}>
                <AppBar
                    position="fixed"
                    sx={{
                        width: { sm: `calc(100% - ${drawerWidth}px)` },
                        ml: { sm: `${drawerWidth}px` },
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                    }}
                >
                    <Toolbar>
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{ mr: 2, display: { sm: 'none' } }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Typography variant="h6" noWrap component="div" color="black">
                            SAIL Mentor
                        </Typography>
                    </Toolbar>
                </AppBar>
                <Box
                    component="nav"
                    sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
                    aria-label="navigation drawers"
                >
                    <Drawer
                        variant="temporary"
                        open={isDrawerExpanded}
                        onClose={handleDrawerToggle}
                        ModalProps={{ keepMounted: true }}
                        sx={{
                            display: { xs: 'block', sm: 'none' },
                            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                        }}
                    >
                        {drawer}
                    </Drawer>
                    <Drawer
                        variant="permanent"
                        sx={{
                            display: { xs: 'none', sm: 'block' },
                            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                        }}
                        open
                    >
                        {drawer}
                    </Drawer>
                </Box>
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        width: { sm: `calc(100% - ${drawerWidth}px)` },
                        height: '100vh', // Full viewport height
                        // overflowY: 'auto', // Enable vertical scrolling
                        boxSizing: 'border-box', // Include padding in height
                    }}
                >
                    <Toolbar />
                    <SnackbarContext.Provider value={{ showSnackbar }}>
                        <Routes>
                            <Route path="home" element={<Home />} />
                            <Route path="/courses/:id" element={<CourseRouteGuard />} />
                            <Route path="/courses/:id/form" element={<CourseInfoForm onClose={handleCoursePlanFormReturn} />} />
                            <Route path="/courses/:id/chat" element={<CourseChatTable />} />
                            <Route path="/courses/:id/chat/evaluation/:chat_id" element={<EvaluationPage />} />
                            <Route path="/courses/:id/chat/:chat_id" element={<SuggestionPage />} />
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </SnackbarContext.Provider>
                </Box>
            </Box>
            <CourseDialog
                open={conversationModalOpen}
                onClose={handleCourseDialogClose}
                onCreate={handleConversationModalCreate}
                course={conversation}
            />
            <ConfirmDialog
                openDialog={confirmDialogOpen}
                onConfirm={handleDeleteCourse}
                onClose={handleConfirmDialogClose}
                text={deleteDialog}
            />
            <Snackbar
                open={snackbarState.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbarState.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbarState.message}
                </Alert>
            </Snackbar>
        </ThemeProvider>
    );
};

export default DashBoard;